/**
 * Le contrat de la **quarantaine des commissions**, côté navigateur
 * (`EX-TAR-014`).
 *
 * **Ce fichier ne décide de rien.** Il transporte, et il déclare ce que le
 * serveur rend — le type suit `CommissionsApi.QuarantineView`, sans rien y
 * ajouter ni rien en retirer. `test/app/dashboard-maquette.test.tsx` confronte
 * champ par champ cette déclaration au **fichier Java lui-même** : recopier un
 * contrat, c'est en faire une seconde source de vérité qui vieillira (`D-35`,
 * `D-45`).
 *
 * ## Une file qui était servie, éprouvée, et appelée par aucun écran
 *
 * `GET /api/backoffice/commissions/quarantine` rend les commissions
 * **retenues** : celles qu'un contrôle a mises de côté et qui attendent une
 * décision humaine avant tout versement. La route existait, la table existait,
 * et rien ne la lisait — la famille de défaut que `D-25`, `D-30`, `D-35`,
 * `D-45` et `D-57` décrivent.
 *
 * Elle **répondait 400 à chaque appel** jusqu'à cette nuit : son `SELECT`
 * nommait `o.code` là où `network.outlet` porte `outlet_code`. Ce paquet est
 * écrit contre le *contrat*, pas contre le symptôme ; il n'a rien à rattraper
 * quand la route se met à répondre.
 *
 * ## Ce que la file porte, et ce que le TABLEAU DE BORD n'en montre pas
 *
 * Le contrat sert sept champs, et trois d'entre eux — `reasonCode`,
 * `reasonLabel`, `detail` — **disent le scénario de détection**. Ils sont
 * déclarés ici parce que le serveur les sert et qu'un client qui les tairait
 * mentirait sur le contrat.
 *
 * **Le tableau de bord ne les rend pas**, et c'est une décision du porteur qui
 * ne se renégocie pas : l'écran du guichetier ne révèle jamais le motif de
 * détection — le dire apprendrait à le contourner —, et une file de back-office
 * qui l'afficherait à côté de comptes annulerait cette précaution. Le motif se
 * lit **dans le dossier**, par qui décide. Voir `DashboardScreen`, et le cas de
 * `test/app/dashboard.test.tsx` qui l'éprouve.
 *
 * ## Les montants sont des ENTIERS D'UNITÉS MINEURES, et sans devise
 *
 * `amount` est un `long` de unités mineures, et **aucun code de devise n'est
 * servi**. Écrire « F CFA » ici choisirait une devise que personne n'a rendue.
 */

import { ApiError } from './api-client.js';
import type { ApiClient } from './api-client.js';

/** Le chemin de la file, tel que `CommissionsApi` le sert. */
export const quarantinePath = '/api/backoffice/commissions/quarantine';

/**
 * Le motif d'audit de la consultation de la file.
 *
 * Une **constante** et non une saisie, pour la raison qui la rend constante sur
 * la file de double validation et sur les quatre lectures du rapprochement :
 * on ouvre toujours cette file pour la même raison — voir ce qui attend une
 * décision. Le serveur refuse un motif vide (`requireReason`).
 */
export const quarantineAuditReason = 'file-de-quarantaine-des-commissions';

/**
 * Une commission en quarantaine, exactement telle que le serveur la rend.
 *
 * Sept champs, et sept seulement. `outletCode` est la référence **publique** du
 * guichet — `GD3REEL` —, pas son `UUID` : le contrat HTTP laisse tomber
 * `outletId`, `customerId`, et les trois champs de décision, qui n'ont de sens
 * que pour qui instruit le dossier.
 *
 * `reasonCode`, `reasonLabel` et `detail` portent le **scénario de détection**.
 * Voir l'en-tête : le tableau de bord ne les affiche pas.
 */
export interface QuarantinedCommission {
  readonly commissionId: string;
  readonly outletCode: string;
  readonly amount: number;
  readonly reasonCode: string;
  readonly reasonLabel: string;
  readonly detail: string;
  readonly detectedAt: string;
}

/**
 * Lit la file de quarantaine.
 *
 * **Elle est complète** : `QuarantineRepository.findPending` ne porte ni borne
 * ni curseur — `WHERE q.decision IS NULL`, ordonné par date de détection. Le
 * nombre de lignes reçues est donc bien le nombre de commissions en attente de
 * décision ; ce n'est pas un total inventé par l'écran.
 *
 * Aucune capture d'erreur : l'`ApiError` du client porte déjà sa nature et sa
 * clé de message. L'avaler pour rendre une liste vide ferait confondre « aucune
 * commission n'est retenue » — un fait — avec « le serveur a refusé », qui n'en
 * est pas un. Sur une file de retenue, la confusion se lirait comme « rien
 * n'attend de décision », c'est-à-dire l'inverse de ce qui s'est passé.
 */
export async function fetchQuarantine(
  client: ApiClient,
  reason: string = quarantineAuditReason,
): Promise<readonly QuarantinedCommission[]> {
  const recue = await client.request<unknown>(
    `${quarantinePath}?reason=${encodeURIComponent(reason)}`,
  );
  if (!estUneFile(recue)) {
    throw new ApiError({
      kind: 'server',
      status: null,
      correlationId: client.lastCorrelationId ?? '',
    });
  }
  return recue;
}

/**
 * La forme attendue, ligne par ligne. Un `unknown` non vérifié n'entre pas dans
 * l'écran.
 *
 * Une réponse à moitié formée — un objet là où un tableau est attendu, une
 * ligne sans identifiant — ferait lever l'écran au rendu, c'est-à-dire une
 * carte blanche au lieu d'un compte. Rendre une erreur typée fait dire « le
 * serveur a répondu autre chose qu'une file », et n'est jamais confondu avec
 * « rien n'est en quarantaine ».
 */
function estUneFile(recue: unknown): recue is readonly QuarantinedCommission[] {
  return Array.isArray(recue) && recue.every(estUneCommission);
}

function estUneCommission(ligne: unknown): ligne is QuarantinedCommission {
  if (typeof ligne !== 'object' || ligne === null) return false;
  const candidate = ligne as Record<string, unknown>;
  return (
    typeof candidate.commissionId === 'string' &&
    typeof candidate.outletCode === 'string' &&
    typeof candidate.amount === 'number' &&
    typeof candidate.reasonCode === 'string' &&
    typeof candidate.detectedAt === 'string'
  );
}
